import { db } from "services/firebase";
import { getReleasedVoxPontEngine } from "features/voxpontEngine/voxpontEngineRegistry";

/**
 * Product-origin values a VoxPont user can be stamped with.
 *  - "voxpont"       standard VoxPont sign-up
 *  - "voxpont_byoi"  Bring-Your-Own-Institution acquisition channel
 * Must stay in sync with VOXPONT_PRODUCT_ORIGINS in
 * functions/src/auth/voxpontClaims.js and the users-create Firestore rule.
 */
export const VOXPONT_PRODUCT_ORIGINS = ["voxpont", "voxpont_byoi"];

/**
 * Resolve an `origin` query-param value to a product-origin slug.
 * @param {string|null|undefined} originParam
 * @returns {'voxpont' | 'voxpont_byoi'}
 */
export function productOriginFromParam(originParam) {
  return originParam === "byoi" || originParam === "voxpont_byoi"
    ? "voxpont_byoi"
    : "voxpont";
}

/**
 * Stamps the user's Firestore profile with the product they signed up through.
 * Call this once, on first sign-up only. Uses merge:true so it is safe to call
 * on existing users without overwriting other fields.
 *
 * @param {string} uid
 * @param {'voxpont' | 'voxpont_byoi' | null} product
 */
export async function setUserProductOrigin(uid, product) {
  if (product !== null && !VOXPONT_PRODUCT_ORIGINS.includes(product)) {
    throw new Error(`Invalid product origin: ${product}`);
  }
  await db.collection("users").doc(uid).set({ product }, { merge: true });
}

/**
 * Atomically stamps the VoxPont acquisition channel and released engine preference.
 * The caller must pass a registry-released engine; silently storing internal routes
 * would bypass the authentication chooser's release gate.
 */
export async function setVoxPontAuthPreferences(uid, product, engineId) {
  if (!VOXPONT_PRODUCT_ORIGINS.includes(product)) {
    throw new Error(`Invalid product origin: ${product}`);
  }
  const engine = getReleasedVoxPontEngine(engineId);
  if (!engine) {
    throw new Error(`Invalid or unreleased VoxPont engine: ${engineId}`);
  }
  await db.collection("users").doc(uid).set(
    {
      product,
      voxpontEngine: engine.id,
    },
    { merge: true }
  );
}

/**
 * Persists the VoxPont product the user chose as their default landing dashboard.
 * Stored as a product slug (e.g. "medical"). Uses merge:true so it never clobbers
 * other profile fields, and is safe to call repeatedly when the user switches.
 *
 * @param {string} uid
 * @param {string} slug
 */
export async function setVoxPontProduct(uid, slug) {
  await db
    .collection("users")
    .doc(uid)
    .set({ voxpontProduct: slug }, { merge: true });
}
